import type { Signal, SignalType, SignalFilters } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type { SignalsDescription } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsDescription";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";
import type { CustomAttributes } from "app/services/track/track";
import type { track as TrackFn } from "pro-features/components/WithTrack/src/context";

import getCustomAttributesFromSignalFilters, { getSignalCreateEventName, getSignalEditEventName } from "./helpers";
import type { AddSignalEventOrigin, DeleteEventType } from "./types";

export default function createSignalsmanagementTrackingService(track: TrackFn) {
    const trackSignalsManagementEvent = (action: string, name: string) => {
        track("signals management", action, name);
    };

    const trackCreateSignalButtonEvent = (action: string, name: string) => {
        track("add signal button", action, name);
    };

    const trackCreateSignalPopupEvent = (action: string, name: string, customAttributes?: CustomAttributes) => {
        track("add signal popup", action, name, customAttributes);
    };

    const trackEditSignalPopupEvent = (action: string, name: string, customAttributes?: CustomAttributes) => {
        track("edit signal popup", action, name, customAttributes);
    };

    return {
        trackCreateSignalClick(eventOrigin: AddSignalEventOrigin, signalType: SignalType) {
            trackCreateSignalButtonEvent("click add signal", `add new signal/${eventOrigin}/${signalType}`);
        },
        trackCreateSignalEvent(signal: Signal, signalsDescription: Readonly<SignalsDescription> | null) {
            const formattedSignalType = signal.signalType.toLowerCase();
            const eventName = getSignalCreateEventName(signal);
            const filterAttributes = getCustomAttributesFromSignalFilters(signal, signalsDescription);

            trackCreateSignalPopupEvent(`add ${formattedSignalType} signal`, eventName, filterAttributes);
        },
        trackCreateCancelSignalEvent(signalType: SignalType) {
            trackCreateSignalPopupEvent(`close add`, `close signal add/${signalType}`);
        },
        trackCreateReturnSignalEvent(signalType: SignalType) {
            trackCreateSignalPopupEvent(`return from add`, `return from signal add/${signalType}`);
        },

        trackEditSignalClick(signal: UISignal<SignalFilters>) {
            trackSignalsManagementEvent(
                "edit signal",
                `select edit signal/${signal.template.alias}/${signal.signalType}`,
            );
        },
        trackEditSignalEvent(
            updatedSignal: Signal,
            initialSignal: Signal,
            signalsDescription: Readonly<SignalsDescription> | null,
        ) {
            const formattedSignalType = updatedSignal.signalType.toLowerCase();
            const filterAttributes = getCustomAttributesFromSignalFilters(updatedSignal, signalsDescription);

            trackEditSignalPopupEvent(
                `edit ${formattedSignalType} signal`,
                getSignalEditEventName(updatedSignal, initialSignal),
                filterAttributes,
            );
        },
        trackEditCancelSignalEvent(signal: Signal) {
            const { alias = "unknown-signal" } = signalsService.getSignalTemplate(signal) ?? {};

            trackEditSignalPopupEvent(`close edit`, `close signal edit/${signal.signalType}/${alias}`);
        },
        trackEditReturnSignalEvent(signal: Signal) {
            const { alias = "unknown-signal" } = signalsService.getSignalTemplate(signal) ?? {};

            trackEditSignalPopupEvent(`return from edit`, `return from signal edit/${signal.signalType}/${alias}`);
        },

        trackDeleteSignalClick(signal: UISignal<SignalFilters>) {
            trackSignalsManagementEvent(
                "click delete signal",
                `select delete signal/${signal.template.alias}/${signal.signalType}`,
            );
        },
        trackDeleteSignalEvent(eventType: DeleteEventType) {
            trackSignalsManagementEvent("delete signal", eventType);
        },

        trackSignalTypeClick(signalType: SignalType) {
            trackSignalsManagementEvent("select signal type", `show signal type/${signalType}`);
        },
    };
}
